import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/Header/Header";
import images from "../../../images";
import Button from "../../../components/common/Button/Button";
import CommonModal from "../../../components/common/Modal/ModalCommon";
import {
  useGetUserInfoQuery,
  useUpdateUserInfoMutation,
  useGetListBusinessLocationQuery,
  usePostFindNewLocationMutation,
} from "../../../services/apiSlice";
import { toast } from "react-toastify";
import { FormField } from "../../../components/common/Input/Input";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import {
  GoogleMap,
  Marker,
  useLoadScript,
  Autocomplete,
} from "@react-google-maps/api";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";

// Define libraries as a static constant outside the component
const LIBRARIES = ["places"];

function EditUser() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const updateModalRef = useRef();
  const addLocationModalRef = useRef();
  const autocompleteRef = useRef(null); // Ref for Autocomplete

  const { data: userInfo, isLoading: isLoadingUserInfo } =
    useGetUserInfoQuery();
  const [updateUserInfo, { isLoading: isUpdating }] =
    useUpdateUserInfoMutation();
  const { data: locationListData, isLoading: isLoadingLocationListData } =
    useGetListBusinessLocationQuery();
  const [findNewLocation, { isLoading: isFindingLocation }] =
    usePostFindNewLocationMutation();

  const [formData, setFormData] = useState({
    company_name: "",
    description: "",
    type_of_food: "",
    website: "",
  });

  const containerStyle = {
    width: "100%",
    height: "180px",
    borderRadius: "12px",
  };

  const [locations, setLocations] = useState([]);
  const [deletedLocationIds, setDeletedLocationIds] = useState([]);
  const [originalLocations, setOriginalLocations] = useState([]);
  const [newLocation, setNewLocation] = useState({
    name: "",
    country: "",
    city: "",
    district: "",
    address: "",
    latitude: "",
    longitude: "",
  });

  const [isManualSearch, setIsManualSearch] = useState(false); // Flag to track manual search

  useEffect(() => {
    if (userInfo?.data) {
      setFormData({
        company_name: userInfo.data.company_name || "",
        description: userInfo.data.description || "",
        type_of_food: userInfo.data.type_of_food || "",
        website: userInfo.data.website || "",
      });
    }
  }, [userInfo]);

  useEffect(() => {
    if (locationListData?.data) {
      setLocations(locationListData.data);
      setOriginalLocations(locationListData.data);
    }
  }, [locationListData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place && place.geometry) {
      const addressComponents = place.address_components || [];
      let country = "";
      let city = "";
      let district = "";
      let address = place.formatted_address || "";

      addressComponents.forEach((component) => {
        if (component.types.includes("country")) {
          country = component.long_name;
        }
        if (component.types.includes("locality")) {
          city = component.long_name;
        }
        if (
          component.types.includes("sublocality") ||
          component.types.includes("administrative_area_level_2")
        ) {
          district = component.long_name;
        }
      });

      setNewLocation((prev) => ({
        ...prev,
        address,
        country,
        city,
        district,
        latitude: place.geometry.location.lat().toString(),
        longitude: place.geometry.location.lng().toString(),
      }));
      setIsManualSearch(false); // Reset manual search flag since Autocomplete was used
    } else {
      toast.warn(t("profile.invalidAddressSelected"));
    }
  };

  const handleNewLocationChange = (e, field) => {
    setNewLocation((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    if (field === "address") {
      setIsManualSearch(true); // Mark as manual search if user types address
    }
  };

  const handleFindNewLocation = useCallback(async () => {
    if (!newLocation.address) return;

    try {
      const data = { text: newLocation.address };
      const response = await findNewLocation(data).unwrap();

      if (response?.data) {
        setNewLocation((prev) => ({
          ...prev,
          ...response.data,
        }));
      }
    } catch (error) {
      toast.error(t("toast.error.failedToFindLocation"));
    }
  }, [findNewLocation, t, newLocation.address]);

  useEffect(() => {
    if (!newLocation.address || !isManualSearch) return;

    const debouncedFind = debounce(() => {
      handleFindNewLocation();
    }, 800);

    debouncedFind();

    return () => {
      debouncedFind.cancel();
    };
  }, [newLocation.address, handleFindNewLocation, isManualSearch]);

  const addressMap = {
    lat: Number(newLocation?.latitude) || 10.99835602,
    lng: Number(newLocation?.longitude) || 77.01502627,
  };
  const defaultCenter = {
    lat: 10.99835602,
    lng: 77.01502627,
  };

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  if (loadError) {
    return (
      <div className="p-4 text-red-500">
        {t("base.errorLoadingMap")} - Please check your Google Maps API key and
        ensure the Places API is enabled.
      </div>
    );
  }

  if (!isLoaded) {
    return <LoadingCommon />;
  }

  const handleOpenUpdateModal = () => {
    updateModalRef?.current?.openModal();
  };

  const handleEditLocation = (location) => {
    setNewLocation({
      id: location.id,
      name: location.name || "",
      country: location.country || "",
      city: location.city || "",
      district: location.district || "",
      address: location.address || "",
      latitude: location.latitude || "",
      longitude: location.longitude || "",
    });
    setIsManualSearch(false); // Reset manual search flag when editing
    addLocationModalRef?.current?.openModal();
  };

  const handleAddNewLocation = () => {
    setNewLocation({
      name: "",
      country: "",
      city: "",
      district: "",
      address: "",
      latitude: "",
      longitude: "",
    });
    setIsManualSearch(false); // Reset manual search flag when adding new
    addLocationModalRef?.current?.openModal();
  };

  const handleSaveLocation = () => {
    if (
      !newLocation.address ||
      !newLocation.latitude ||
      !newLocation.longitude
    ) {
      toast.error(t("profile.pleaseSelectValidAddress"));
      return;
    }

    const isDuplicate = locations.some(
      (loc) =>
        loc.id !== newLocation.id &&
        (loc.address.toLowerCase().trim() ===
          newLocation.address.toLowerCase().trim() ||
          (loc.name &&
            newLocation.name &&
            loc.name.toLowerCase().trim() ===
              newLocation.name.toLowerCase().trim()))
    );

    if (isDuplicate) {
      toast.error(t("companyProfile.locationAlreadyExists"));
      return;
    }

    const locationData = {
      ...newLocation,
    };

    if (newLocation.id) {
      setLocations((prev) =>
        prev.map((loc) => (loc.id === newLocation.id ? locationData : loc))
      );
    } else {
      locationData.id = Date.now().toString();
      setLocations((prev) => [...prev, locationData]);
    }

    addLocationModalRef?.current?.closeModal();
  };

  const handleDeleteLocation = (locationId) => {
    setLocations((prev) => prev.filter((loc) => loc.id !== locationId));
    if (!deletedLocationIds.includes(locationId)) {
      setDeletedLocationIds((prev) => [...prev, locationId]);
    }
  };

  const handleUpdate = async () => {
    try {
      const newLocations = locations.filter((loc) => {
        return !originalLocations.some((orig) => orig.id === loc.id);
      });

      const updateLocations = locations
        .filter((loc) =>
          originalLocations.some(
            (orig) =>
              orig.id === loc.id && JSON.stringify(orig) !== JSON.stringify(loc)
          )
        )
        .map((loc) => ({
          id: loc.id,
          name: loc.name,
          country: loc.country,
          city: loc.city,
          district: loc.district,
          address: loc.address,
          latitude: loc.latitude,
          longitude: loc.longitude,
        }));

      const payload = {
        company_name: formData.company_name,
        description: formData.description,
        type_of_food: formData.type_of_food,
        website: formData.website,
        delete_locations: deletedLocationIds,
        update_locations: updateLocations,
        new_locations: newLocations.map((loc) => ({
          name: loc.name,
          country: loc.country || "",
          city: loc.city || "",
          district: loc.district || "",
          address: loc.address,
          latitude: loc.latitude,
          longitude: loc.longitude,
        })),
      };

      const response = await updateUserInfo(payload).unwrap();

      if (
        response.message === "Operation successful" ||
        response.status === 200
      ) {
        const updatedLocations = locations.filter(
          (loc) => !deletedLocationIds.includes(loc.id)
        );
        setLocations(updatedLocations);
        setOriginalLocations(updatedLocations);
        setDeletedLocationIds([]);
        toast.success(t("profile.profileUpdated"));
        updateModalRef?.current?.closeModal();
        navigate("/profile");
      }
    } catch (err) {
      toast.error(
        err?.data?.message ||
          err?.data?.detail[0].msg ||
          t("toast.error.failedToUpdateProfile")
      );
    }
  };

  return (
    <main className="flex flex-col mx-auto w-full bg-neutral-50 max-w-[480px] min-h-screen">
      <LoadingCommon
        statusLoading={
          isLoadingLocationListData || isLoadingUserInfo || isFindingLocation
        }
      />
      <section className="sticky top-0 z-10 w-full bg-[#3F54DF] rounded-none min-h-[110px]">
        <Header
          title={t("profile.editProfile")}
          backIcon={images.BackIcon}
          onBackClick={() => navigate(-1)}
        />
      </section>
      <form className="flex flex-col p-3.5 w-full">
        <div className="w-full">
          <FormField
            icon={images.BuildingIcon}
            label={t("profile.companyName")}
            name="company_name"
            value={formData.company_name}
            onChange={handleInputChange}
            className="mt-4"
          />
          <FormField
            icon={images.BookmarkIcon}
            label={t("profile.description")}
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className="mt-4"
            typeInput="textarea"
          />
          <FormField
            icon={images.TagIcon}
            label={t("profile.typeOfFood")}
            name="type_of_food"
            value={formData.type_of_food}
            onChange={handleInputChange}
            className="mt-4"
            typeInput="textarea"
          />
          <FormField
            icon={images.GlobalIcon}
            label={t("profile.website")}
            name="website"
            value={formData.website}
            onChange={handleInputChange}
            className="mt-4"
          />
          <section className="mt-4 w-full font-medium text-zinc-800">
            <div className="flex justify-between items-center w-full whitespace-nowrap text-neutral-500">
              <label className="flex items-center text-sm leading-none">
                <img
                  src={images.MapPointIcon}
                  alt="Location"
                  className="w-[22px] h-[22px] mr-2"
                />
                <span>{t("profile.location")}</span>
              </label>
              <button
                type="button"
                onClick={handleAddNewLocation}
                className="flex items-center px-2 py-1.5 text-xs bg-white rounded-md border border-stone-200 min-h-8"
              >
                <img
                  src={images.AddCircleIcon}
                  alt="Add"
                  className="w-[18px] h-[18px] mr-1"
                />
                <span>{t("base.add")}</span>
              </button>
            </div>
            <ul className="mt-1.5 space-y-1.5">
              {locations
                .slice()
                .sort((a, b) => {
                  const nameA = a.name
                    ? a.name.toLowerCase()
                    : a.address.toLowerCase();
                  const nameB = b.name
                    ? b.name.toLowerCase()
                    : b.address.toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map((location) => (
                  <li
                    key={location.id}
                    className="flex justify-between items-center px-2.5 py-1.5 w-full text-sm rounded-xl bg-gray-100"
                  >
                    <span
                      onClick={() => handleEditLocation(location)}
                      className="cursor-pointer hover:text-indigo-600"
                    >
                      {location.name ? location.name : location.address}
                    </span>
                    <img
                      src={images.TrashLocationIcon}
                      alt="Delete"
                      className="w-[24px] h-[24px] cursor-pointer hover:opacity-75"
                      onClick={() => handleDeleteLocation(location.id)}
                    />
                  </li>
                ))}
            </ul>
          </section>
        </div>
        <div className="flex gap-3.5 mt-6 w-full">
          <Button
            className="flex-1 px-4 py-3 text-base font-medium bg-white rounded-xl border border-stone-200 text-neutral-500"
            text={t("base.cancel")}
            onClick={() => navigate(-1)}
          />
          <Button
            className="flex-1 px-4 py-3 text-base font-semibold text-white bg-[#3F54DF] rounded-xl disabled:opacity-50"
            text={t("base.save")}
            onClick={handleOpenUpdateModal}
            disabled={isUpdating}
          />
        </div>
      </form>

      <CommonModal
        ref={updateModalRef}
        title="Are you sure you want to update your profile information?"
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 px-4 py-2 rounded-xl bg-white border border-stone-200 text-neutral-500"
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.update")}
              className="flex-1 px-4 py-2 rounded-xl text-white bg-[#3F54DF] disabled:opacity-50"
              variant="primary"
              onClick={handleUpdate}
              disabled={isUpdating}
            />
          </>
        )}
      >
        <div className="flex flex-col justify-center items-center">
          <img
            src={images.UpdateModalImage}
            alt="Update"
            className="w-[80px]"
          />
        </div>
      </CommonModal>

      <CommonModal
        ref={addLocationModalRef}
        title={
          newLocation.id
            ? t("companyProfile.editLocation")
            : t("companyProfile.addNewLocation")
        }
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 px-5 py-3 rounded-xl bg-white border border-stone-200 text-neutral-500"
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.save")}
              className="flex-1 px-5 py-3 rounded-xl text-white bg-indigo-600"
              variant="primary"
              onClick={handleSaveLocation}
            />
          </>
        )}
      >
        <div className="flex flex-col gap-3">
          <input
            type="text"
            className="w-full p-2 border border-gray-200 rounded-md"
            placeholder={t("profile.locationName")}
            value={newLocation.name}
            onChange={(e) => handleNewLocationChange(e, "name")}
          />

          <Autocomplete
            onLoad={(autocomplete) => {
              autocompleteRef.current = autocomplete;
            }}
            onPlaceChanged={handlePlaceChanged}
            options={{
              types: ["address"], // Restrict to addresses only
              fields: ["address_components", "formatted_address", "geometry"], // Specify fields to return
            }}
          >
            <div className="relative w-full">
              <input
                type="text"
                className="w-full p-2 border border-gray-200 rounded-md"
                placeholder={t("profile.searchAddress")}
                value={newLocation.address}
                onChange={(e) => handleNewLocationChange(e, "address")}
              />
              <img
                className="absolute w-[14px] h-[14px] top-1/2 right-2 transform -translate-y-1/2 cursor-pointer"
                src={images.SearchIcon}
                alt=""
                onClick={handleFindNewLocation}
              />
            </div>
          </Autocomplete>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={
              newLocation?.latitude && newLocation?.longitude
                ? addressMap
                : defaultCenter
            }
            zoom={15}
            onLoad={(map) => {}}
          >
            {newLocation?.latitude && newLocation?.longitude && (
              <Marker
                position={addressMap}
                icon={{
                  url: images.DotMapIconBlue,
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
              />
            )}
          </GoogleMap>
        </div>
      </CommonModal>
    </main>
  );
}

export default EditUser;
